import React from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';

import { BRAND } from '../brand';
import { useNetworkStatus, installNetworkInterceptor } from '../native/network';

/**
 * The screen App Review sees in airplane mode.
 *
 * There is no service worker in this app, so with no network a WKWebView renders
 * its own "cannot open the page" error — which reads as a broken app and is a
 * direct guideline 4.2 rejection risk. This replaces it with something branded
 * that explains the state and offers a way out.
 *
 * Rendered as a full-screen overlay rather than a route swap so the app stays
 * mounted underneath: coming back from a tunnel returns the user to the page they
 * were on, with their form state intact.
 */

export interface OfflineScreenProps {
  onRetry?: () => void;
  /** Drives the button's spinner while a connectivity probe is in flight. */
  checking?: boolean;
}

export const OfflineScreen: React.FC<OfflineScreenProps> = ({ onRetry, checking = false }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="offline-heading"
    className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-8 text-center
               bg-gradient-to-br from-primary-50 via-blue-50 to-primary-100
               dark:from-gray-900 dark:via-gray-900 dark:to-gray-800"
    // The overlay covers the notch and home indicator, so it owns the insets.
    style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}
  >
    <div className="relative">
      <img
        src={BRAND.logoSrc}
        alt=""
        aria-hidden="true"
        className="w-[72px] h-[72px] rounded-[20px] shadow-soft-lg"
      />
      <span
        className="absolute -bottom-1 -right-1 flex items-center justify-center w-8 h-8 rounded-full
                   bg-white dark:bg-gray-800 shadow-soft text-gray-500 dark:text-gray-400"
      >
        <WifiOff className="w-4 h-4" aria-hidden="true" />
      </span>
    </div>

    <h1
      id="offline-heading"
      className="mt-6 text-2xl font-extrabold tracking-[-0.02em] text-gray-900 dark:text-gray-100"
    >
      You're offline
    </h1>
    <p className="mt-2 max-w-xs text-base text-gray-600 dark:text-gray-400">
      {BRAND.name} needs a connection to load your matches. Check your Wi-Fi or cellular data and
      try again.
    </p>

    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        disabled={checking}
        className="mt-8 inline-flex items-center justify-center gap-2 w-full max-w-xs min-h-[44px]
                   px-5 py-3 rounded-xl font-semibold text-white shadow-soft-md
                   bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600
                   transition-colors duration-200 ease-smooth disabled:opacity-70"
      >
        <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} aria-hidden="true" />
        {checking ? 'Checking…' : 'Try again'}
      </button>
    )}
  </div>
);

/**
 * Wraps the app: keeps `children` mounted and drops the offline overlay on top
 * whenever connectivity is lost. Also installs the axios interceptor that feeds
 * failed-request detection, ejecting it on unmount.
 */
export const OfflineGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { online, checking, retry } = useNetworkStatus();

  React.useEffect(() => installNetworkInterceptor(), []);

  const handleRetry = React.useCallback(() => {
    void retry();
  }, [retry]);

  return (
    <>
      {children}
      {!online && <OfflineScreen onRetry={handleRetry} checking={checking} />}
    </>
  );
};

export default OfflineScreen;
